import { MultilineInput } from '@amelio/ui-library';

import { useAmelioTranslations } from '@/hooks';
import i18n from '@/plugins/i18n';

/**
 * UI-only props.
 * Stateless and reusable.
 */
export interface PlainTextQuestionAnswerProps {
    /** Current text values (array with single value for consistency) */
    selectedValues?: string[];

    /** Fires on every text change */
    onChange: (values: string[]) => void;

    /** Accessible label linked to question text */
    ariaLabel?: string;

    /** Optional tooltip text */
    tooltipContent?: string;

    /** Fires when Enter is pressed (without Shift) — e.g. go to next question */
    onEnterPress?: () => void;

    /** Auto-focus the input on mount */
    autoFocus?: boolean;

    /** Disable input */
    disabled?: boolean;

    /** Max character length */
    maxLength?: number;

    /** Optional className for layout customization */
    className?: string;

    /** Test id */
    dataTestId?: string;
}

/**
 * Dumb / Presentational component.
 *
 * Renders a multiline text input with optional tooltip support.
 * TooltipProvider must be available at app or story level.
 */
export const PlainTextQuestionAnswer = ({
    selectedValues = [],
    onChange,
    ariaLabel = 'Text input',
    tooltipContent,
    onEnterPress,
    disabled = false,
    autoFocus = false,
    maxLength,
    className = '',
    dataTestId,
}: PlainTextQuestionAnswerProps) => {
    const { t } = useAmelioTranslations();
    const isRtl = i18n.language.startsWith('ar');
    const value = selectedValues[0] ?? '';

    const handleChange = (newValue: string) => {
        onChange([newValue]);
    };

    return (
        <div className={`w-full ${className}`}>
            <MultilineInput
                value={value}
                onChange={handleChange}
                dir={isRtl ? 'rtl' : 'auto'}
                ariaLabel={ariaLabel}
                disabled={disabled}
                autoFocus={autoFocus}
                maxLength={maxLength}
                dataTestId={dataTestId}
                size={'sm'}
                rows={3}
                autoGrow
                showTooltip
                onEnterPress={onEnterPress}
                placeholder={t('reviewAnswer.plainTextPlaceholder')}
                tooltipText={tooltipContent ?? t('reviewAnswer.plainTextTooltip')}
                className={
                    'rounded-xl border-white bg-white/10 text-white placeholder:text-white/50'
                }
            />
        </div>
    );
};
